/**
 * Authentication provider for Refine
 * Handles login, logout, and session management with coreBOS
 */

import type { AuthProvider } from "@refinedev/core";
import axios from "axios";
import { loadContextsFromServer } from "@/hooks/useContextStore";

// User type
export interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
}

// Configure axios to send cookies
axios.defaults.withCredentials = true;

export const authProvider: AuthProvider = {
  login: async ({ username, access_key }) => {
    try {
      const response = await axios.post("/api/auth/login", {
        username,
        access_key,
      });

      if (response.data.success) {
        // Store user in localStorage for quick access
        localStorage.setItem("user", JSON.stringify(response.data.user));

        // Load contexts from server (async, don't block login)
        loadContextsFromServer().catch(() => {});

        return {
          success: true,
          redirectTo: "/",
        };
      }

      return {
        success: false,
        error: {
          name: "LoginError",
          message: response.data.error || "Invalid credentials",
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          name: "LoginError",
          message:
            error.response?.data?.error || error.message || "Login failed",
        },
      };
    }
  },

  logout: async () => {
    try {
      await axios.post("/api/auth/logout");
    } catch (error) {
      // Ignore logout errors
    }

    localStorage.removeItem("user");

    return {
      success: true,
      redirectTo: "/login",
    };
  },

  check: async () => {
    try {
      const response = await axios.get("/api/auth/me");

      if (response.data.success) {
        // Update stored user
        localStorage.setItem("user", JSON.stringify(response.data.user));

        return {
          authenticated: true,
        };
      }

      return {
        authenticated: false,
        redirectTo: "/login",
      };
    } catch (error) {
      return {
        authenticated: false,
        redirectTo: "/login",
      };
    }
  },

  getIdentity: async () => {
    // Try to get from localStorage first (faster)
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return {
          id: user.id,
          name: `${user.firstName} ${user.lastName}`.trim() || user.username,
          avatar: undefined,
        };
      } catch {
        // Fall through to API call
      }
    }

    // Fetch from API
    try {
      const response = await axios.get("/api/auth/me");

      if (response.data.success) {
        const user = response.data.user;
        return {
          id: user.id,
          name: `${user.firstName} ${user.lastName}`.trim() || user.username,
          avatar: undefined,
        };
      }
    } catch (error) {
      // Not authenticated
    }

    return null;
  },

  getPermissions: async () => {
    // For now, return null (no permissions system)
    return null;
  },

  onError: async (error) => {
    if (error.response?.status === 401) {
      return {
        logout: true,
        redirectTo: "/login",
      };
    }

    return { error };
  },
};
